/**
 * Interessenten-Ansicht: Was sehen selbst registrierte Kunden, die Logiful noch nicht freigeschaltet hat?
 * Freigabeliste statt Sperrliste – jede Kunden-Route gehört zu genau einem Bereich; was nicht freigegeben ist, liefert
 * die API nicht (403) und das Portal blendet es aus. Neue Funktionen sind damit automatisch gesperrt, bis Logiful sie
 * einem Bereich zuordnet (ein Test prüft, dass jede Kunden-Route zugeordnet ist).
 * Gilt nur für Kunden mit Selbst-Registrierung (tenants.signup) im Testbetrieb – nie für Bestandskunden.
 */

export type ProspectLevel = "none" | "view" | "edit";

interface ProspectFeature {
  label: string;
  description: string;
  levels: readonly ProspectLevel[];
  /** Standard für alle Interessenten (von Logiful änderbar) */
  default: ProspectLevel;
  /** Kunden-Routen nach /api/tenants/:tenantId/ (Präfix je Segment; das längste passende gewinnt) */
  routes: readonly string[];
  /** Seiten im Portal (Präfix) */
  paths: readonly string[];
}

export const PROSPECT_FEATURES = {
  channels: { label: "Shops verbinden", description: "Shopify, Marktplätze, SFTP verbinden; Datenschutz-Anfragen", levels: ["none", "edit"], default: "edit", routes: ["channels", "privacy-requests"], paths: ["/channels", "/connect/shopify", "/settings/privacy"] },
  products: { label: "Artikel", description: "Stammdaten ansehen und ergänzen", levels: ["none", "view", "edit"], default: "edit", routes: ["products"], paths: ["/products"] },
  orders: { label: "Aufträge", description: "Aufträge aus den Shops (im Testbetrieb gehen keine ans Lager)", levels: ["none", "view", "edit"], default: "view", routes: ["orders", "address-book", "address-check", "import-templates"], paths: ["/orders"] },
  team: { label: "Team & Sicherheit", description: "Kollegen einladen, Benachrichtigungen, Zwei-Faktor, Protokoll", levels: ["none", "edit"], default: "edit", routes: ["members", "invites", "security", "audit", "notifications"], paths: ["/settings/team", "/settings/notifications", "/settings/security", "/settings/audit"] },
  stock: { label: "Bestand", description: "Bestände, MHD, Reichweite, Bestandsabgleich", levels: ["none", "view", "edit"], default: "none", routes: ["stock", "settings/stock-sync", "settings/best-before", "settings/replenishment", "settings/reservation", "settings/backorder"], paths: ["/stock", "/settings/stock-sync"] },
  inbounds: { label: "Wareneingänge & Einkauf", description: "Anlieferungen, Lieferanten, Bestellungen", levels: ["none", "view", "edit"], default: "none", routes: ["inbounds", "suppliers", "purchase-orders", "purchasing"], paths: ["/inbounds", "/purchasing"] },
  returns: { label: "Retouren", description: "Retouren, Klärfälle, Retourenportal", levels: ["none", "view", "edit"], default: "none", routes: ["returns", "return-clarifications", "returns-portal", "orders/:orderId/returns"], paths: ["/returns"] },
  rules: { label: "Regeln", description: "Auftrags-, Versand- und Artikelregeln, Stammdaten-Prüfung, Exportdokumente", levels: ["none", "view", "edit"], default: "none", routes: ["shipping-rules", "product-rules", "settings/validation", "settings/export-document"], paths: ["/rules", "/settings/validation"] },
  shipping: { label: "Versand & Endkunden", description: "Versandkonten, Track & Trace, Tracking-Seite, Lieferzeit im Shop, Kundenkommunikation", levels: ["none", "view", "edit"], default: "none", routes: ["carrier-accounts", "tracking", "tracking-page", "delivery-widget", "customer-comms"], paths: ["/tracking", "/settings/carrier-accounts", "/settings/delivery-widget", "/settings/customer-comms"] },
  modules: { label: "Module & Preise", description: "Buchbare Module, Pakete und Preise", levels: ["none", "view", "edit"], default: "none", routes: ["modules", "module-bundles"], paths: ["/settings/modules"] },
  reports: { label: "Auswertungen", description: "Leistung, Kosten, Profit Center, Kennzahlen", levels: ["none", "view"], default: "none", routes: ["performance", "costs", "profit", "returns/analytics", "orders/kpis", "invoices"], paths: ["/performance", "/costs", "/profit", "/invoices"] },
  operations: { label: "B2B, FBA & Produktion", description: "B2B-Lieferungen, Pre-FBA, Produktion", levels: ["none", "view", "edit"], default: "none", routes: ["b2b", "fba", "production"], paths: ["/b2b", "/fba", "/production"] },
  integrations: { label: "Schnittstellen & Lager", description: "API, Webhooks, Tool-Anbindungen, KI-Assistent, Lageranbindung (FFN)", levels: ["none", "view", "edit"], default: "none", routes: ["api-keys", "webhooks", "apps", "mcp", "backend"], paths: ["/settings/integrations", "/settings/apps", "/settings/ai", "/settings/warehouse"] },
  partners: { label: "Partner Network", description: "Angebote der Logiful-Partner", levels: ["none", "view", "edit"], default: "none", routes: ["partners"], paths: ["/partners"] },
  news: { label: "News & Termine", description: "Ankündigungen und feste Anliefertermine von Logiful", levels: ["none", "view"], default: "none", routes: ["announcements", "calendar"], paths: [] },
} as const satisfies Record<string, ProspectFeature>;

export type ProspectFeatureKey = keyof typeof PROSPECT_FEATURES;
export const PROSPECT_FEATURE_KEYS = Object.keys(PROSPECT_FEATURES) as ProspectFeatureKey[];
export type ProspectAccess = Record<ProspectFeatureKey, ProspectLevel>;

/** Immer erreichbar: Übersicht (eigene Anordnung), Handlungsbedarf, Stammdaten des Kunden */
const ALWAYS_ROUTES = ["", "dashboard", "action-items"];
const ALWAYS_PATHS = ["/", "/todo", "/settings", "/start"];

export const DEFAULT_PROSPECT_ACCESS = Object.fromEntries(PROSPECT_FEATURE_KEYS.map((k) => [k, PROSPECT_FEATURES[k].default])) as ProspectAccess;

/** gespeicherte (ggf. unvollständige) Auswahl → vollständige, gültige Freigaben; fehlende Bereiche = Standard */
export function normalizeProspectAccess(v: unknown, base: ProspectAccess = DEFAULT_PROSPECT_ACCESS): ProspectAccess {
  const o = (v && typeof v === "object" ? v : {}) as Record<string, unknown>;
  return Object.fromEntries(
    PROSPECT_FEATURE_KEYS.map((k) => {
      const l = o[k];
      return [k, typeof l === "string" && (PROSPECT_FEATURES[k].levels as readonly string[]).includes(l) ? l : base[k]];
    }),
  ) as ProspectAccess;
}

/** wirksame Freigaben: Standard von Logiful, darüber die Ausnahmen je Interessent */
export const prospectAccessFor = (defaults: unknown, override: unknown): ProspectAccess => normalizeProspectAccess(override, normalizeProspectAccess(defaults));

const segMatch = (rest: string, r: string) => {
  const a = rest.split("/");
  const b = r.split("/");
  return b.length <= a.length && b.every((s, i) => s === a[i]);
};

/** Bereich einer Kunden-Route (Muster wie /api/tenants/:tenantId/orders/:id) – "always" oder null (nicht zugeordnet) */
export function prospectFeatureOfRoute(pattern: string): ProspectFeatureKey | "always" | null {
  const rest = pattern.replace(/^\/api\/tenants\/:tenantId\/?/, "").replace(/\/$/, "");
  if (ALWAYS_ROUTES.some((r) => (r === "" ? rest === "" : segMatch(rest, r)))) return "always";
  let best: { k: ProspectFeatureKey; len: number } | null = null;
  for (const k of PROSPECT_FEATURE_KEYS)
    for (const r of PROSPECT_FEATURES[k].routes) {
      const len = r.split("/").length;
      if (segMatch(rest, r) && (!best || len > best.len)) best = { k, len };
    }
  return best?.k ?? null;
}

/** Darf ein Interessent diese Route aufrufen? (Lesen = GET/HEAD) */
export function prospectAllowsRoute(access: ProspectAccess, pattern: string, method: string): { ok: true } | { ok: false; feature: ProspectFeatureKey | null } {
  const f = prospectFeatureOfRoute(pattern);
  if (f === "always") return { ok: true };
  if (!f) return { ok: false, feature: null };
  const l = access[f];
  const read = method === "GET" || method === "HEAD";
  return l === "edit" || (l === "view" && read) ? { ok: true } : { ok: false, feature: f };
}

/** Darf ein Interessent diese Portalseite sehen? */
export function prospectAllowsPath(access: ProspectAccess, path: string): boolean {
  const p = path.replace(/\/$/, "") || "/";
  if (ALWAYS_PATHS.includes(p)) return true;
  let best: { k: ProspectFeatureKey; len: number } | null = null;
  for (const k of PROSPECT_FEATURE_KEYS)
    for (const x of PROSPECT_FEATURES[k].paths) if ((p === x || p.startsWith(`${x}/`)) && (!best || x.length > best.len)) best = { k, len: x.length };
  return !!best && access[best.k] !== "none";
}

/** Übersichts-Kacheln je Bereich */
export const PROSPECT_WIDGETS: Record<string, ProspectFeatureKey | "always"> = {
  kpis: "orders", action_items: "always", news: "news", calendar: "news", order_flow: "orders", channels: "channels", reorder: "stock", slow_movers: "stock", inbounds: "inbounds", production: "operations", profit: "reports",
};
export const prospectAllowsWidget = (access: ProspectAccess, widget: string) => {
  const f = PROSPECT_WIDGETS[widget];
  return f === "always" || (!!f && access[f] !== "none");
};
